import {
	FieldArray,
	GetFieldValue,
	SelectField,
	TextInputField,
	useForm,
} from "@sembark-travel/ui-form"
import {
	AutoDistributionXHR,
	relation,
	TOperator,
	TParameter,
	TRuleSet,
} from "./store"
import { Button, Icons, Inline, Select, Stack } from "@sembark-travel/ui-base"
import { useCallback } from "react"
import useSWR from "swr"
import { useXHR } from "@sembark-travel/xhr"

export function GroupRuleSetField() {
	const form = useForm()
	const xhr = useXHR()

	const { data } = useSWR("/routing-rules/create", () =>
		AutoDistributionXHR(xhr).getRoutingRules()
	)

	const getOperator = useCallback(
		(parameter: TParameter) => {
			const type = parameter.type || "any"
			if (data?.data && type) {
				return data.data.operators.filter(
					(d) => d.left_operand_type === type
				)[0]?.operators
			}
			return []
		},
		[data]
	)

	return (
		<FieldArray<Array<TRuleSet>> name="grouped_rule_items">
			{({ fields: groupFields }) => (
				<Stack gap="2">
					{groupFields.map((groupName, groupIndex) => (
						<Stack key={groupName} gap={"4"}>
							<Stack
								borderWidth={"1"}
								borderColor={"muted"}
								padding={"2"}
								bgColor={"inset"}
								gap={"4"}
								rounded="md"
							>
								<FieldArray<TRuleSet> name={groupName}>
									{({ fields }) => (
										<Stack gap="4">
											{fields.map((name, index) => (
												<Stack key={name} gap={"4"}>
													<Inline
														flexWrap={"wrap"}
														justifyContent={"between"}
														gap="2"
													>
														<SelectField
															select={Select}
															name={`${name}.left_operand_name`}
															required
															creatable
															label="Parameter"
															placeholder="Select a condition"
															options={data?.data.parameters}
															labelKey="display_name"
															onChange={(value, fieldName) => {
																form.batch(() => {
																	form.change(
																		fieldName as never,
																		value as never
																	)
																	form.change(
																		`${name}.operator` as keyof TRuleSet,
																		undefined
																	)
																	form.change(
																		`${name}.right_operand_value` as keyof TRuleSet,
																		undefined
																	)
																})
															}}
														/>
														<GetFieldValue<TParameter>
															name={`${name}.left_operand_name`}
														>
															{({ value: parameter }) => {
																return (
																	<>
																		<SelectField
																			select={Select}
																			name={`${name}.operator`}
																			required
																			label="Condition"
																			placeholder="Select a condition"
																			options={getOperator(parameter)}
																			onChange={(value, fieldName) => {
																				form.batch(() => {
																					form.change(
																						fieldName as never,
																						value as never
																					)
																					form.change(
																						`${name}.right_operand_value` as keyof TRuleSet,
																						undefined
																					)
																				})
																			}}
																		/>

																		<Inline alignItems={"stretch"} gap={"1"}>
																			<GetFieldValue<TOperator>
																				name={`${name}.operator`}
																			>
																				{({ value: operator }) => {
																					return [
																						"array<string>",
																						"array<integer>",
																					].includes(
																						operator.right_operand_type
																					) ? (
																						<SelectField
																							select={Select}
																							name={`${name}.right_operand_value`}
																							required
																							label="Value"
																							placeholder="Type Values"
																							creatable
																							fetchOnMount
																							options={parameter.values || []}
																							multiple
																						/>
																					) : ["string", "integer"].includes(
																							operator.right_operand_type
																					  ) ? (
																						<TextInputField
																							label={"Value"}
																							placeholder="Type Values"
																							name={`${name}.right_operand_value`}
																							type={
																								operator.right_operand_type ===
																								"string"
																									? "text"
																									: "number"
																							}
																							required
																						/>
																					) : (
																						<TextInputField
																							label={"Value"}
																							placeholder="Type Values"
																							name={`${name}.right_operand_value`}
																							required
																							disabled
																							readOnly
																						/>
																					)
																				}}
																			</GetFieldValue>
																			{groupFields.length === 1 &&
																			fields.length === 1 ? null : (
																				<Inline gap="2" alignItems={"end"}>
																					<Button
																						inline
																						onClick={() => {
																							if (index) {
																								const rulesValue: TRuleSet[][] =
																									form.getFieldState(
																										"grouped_rule_items"
																									)?.value as TRuleSet[][]
																								const TempRelesData = [
																									...rulesValue,
																								]
																								TempRelesData[groupIndex][
																									index - 1
																								].relation =
																									TempRelesData[groupIndex][
																										index
																									].relation
																								TempRelesData[
																									groupIndex
																								].splice(index, 1)

																								form.change(
																									"grouped_rule_items",
																									[...TempRelesData]
																								)
																							} else {
																								if (fields.length === 1) {
																									groupFields.remove(groupIndex)
																									return false
																								}
																								fields.remove(index)
																							}
																						}}
																					>
																						<Icons.Cancel />
																					</Button>
																				</Inline>
																			)}
																		</Inline>
																	</>
																)
															}}
														</GetFieldValue>
													</Inline>

													{index + 1 === fields.length ? null : (
														<Inline
															justifyContent={"center"}
															alignItems={"center"}
														>
															<SelectField
																select={Select}
																searchable={false}
																bgColor={"warning"}
																color={"warning"}
																name={`${name}.relation`}
																required
																options={relation}
																style={{
																	maxWidth: "70px",
																}}
																size="sm"
																onChange={(e, n) => {
																	if (!e) return
																	form.batch(() => {
																		form.change(n as never, e as never)
																		const rulesValue =
																			form.getFieldState(
																				"grouped_rule_items"
																			)?.value
																		const TempRelesData = rulesValue
																			? [...rulesValue]
																			: []
																		if (e === "AND") {
																			const sliceData = TempRelesData?.[
																				groupIndex
																			]?.splice(
																				index + 1,
																				TempRelesData[groupIndex]?.length -
																					(index + 1)
																			)
																			if (sliceData && sliceData.length > 0) {
																				TempRelesData?.splice(
																					groupIndex + 1,
																					0,
																					[...sliceData]
																				)
																			}
																			form.change("grouped_rule_items", [
																				...(TempRelesData as TRuleSet[][]),
																			])
																		} else {
																			const merged: TRuleSet[] = [
																				...(TempRelesData[groupIndex] ?? []),
																				...(TempRelesData[groupIndex + 1] ??
																					[]),
																			]
																			TempRelesData[groupIndex] = merged
																			TempRelesData.splice(groupIndex + 1, 1)
																			form.change("grouped_rule_items", [
																				...TempRelesData,
																			])
																		}
																	})
																}}
																placeholder="Type Values"
															/>
														</Inline>
													)}
												</Stack>
											))}
										</Stack>
									)}
								</FieldArray>
							</Stack>
							<FieldArray<TRuleSet> name={groupName}>
								{({ fields }) =>
									fields.map((name, index) =>
										(groupIndex + 1 === groupFields.length &&
											index + 1 === fields.length) ||
										index + 1 !== fields.length ? null : (
											<Inline
												justifyContent={"center"}
												alignItems={"center"}
												key={name}
											>
												<SelectField
													select={Select}
													name={`${name}.relation`}
													width={"auto"}
													required
													bgColor={"success"}
													color={"success"}
													options={relation}
													onChange={(e, n) => {
														if (!e) return
														form.batch(() => {
															form.change(n as never, e as never)
															const rulesValue =
																form.getFieldState("grouped_rule_items")?.value
															const TempRelesData = rulesValue
																? [...rulesValue]
																: []
															if (e === "AND") {
																const sliceData = TempRelesData?.[
																	groupIndex
																]?.splice(
																	index + 1,
																	TempRelesData[groupIndex]?.length -
																		(index + 1)
																)
																if (sliceData && sliceData.length > 0) {
																	TempRelesData?.splice(groupIndex + 1, 0, [
																		...sliceData,
																	])
																}
																form.change("grouped_rule_items", [
																	...(TempRelesData as TRuleSet[][]),
																])
															} else {
																const merged: TRuleSet[] = [
																	...(TempRelesData[groupIndex] ?? []),
																	...(TempRelesData[groupIndex + 1] ?? []),
																]
																TempRelesData[groupIndex] = merged
																TempRelesData.splice(groupIndex + 1, 1)
																form.change("grouped_rule_items", [
																	...TempRelesData,
																])
															}
														})
													}}
													placeholder="Type Values"
													style={{
														maxWidth: "70px",
													}}
													size="sm"
												/>
											</Inline>
										)
									)
								}
							</FieldArray>
						</Stack>
					))}
					<Inline>
						<Button
							onClick={() => {
								const newRule: TRuleSet = {
									left_operand_name: null,
									type: "",
									right_operand_value: undefined,
									operator: null,
									relation: "AND",
								}
								groupFields.push([newRule])
							}}
							size="sm"
							status="primary"
						>
							<Icons.Plus /> Add More
						</Button>
					</Inline>
				</Stack>
			)}
		</FieldArray>
	)
}
